import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import RobotFace from "@/components/RobotFace";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AcceptInvite() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [state, setState] = useState({ loading: true, invite: null, error: "" });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    if (!token) { setState({ loading: false, invite: null, error: "Missing invite token." }); return; }
    api.get(`/auth/invite/${token}`)
      .then(r => setState({ loading: false, invite: r.data, error: "" }))
      .catch(e => setState({ loading: false, invite: null, error: formatApiError(e.response?.data?.detail) || "Invite invalid" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/accept-invite", { token, name, password });
      await refresh();
      toast.success("Welcome to the Rammy admin console");
      nav("/admin");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not accept");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="page-accept-invite">
      <div className="hidden md:flex items-center justify-center bg-[var(--rm-ink-900)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{background:"radial-gradient(circle at 30% 40%, rgba(80,227,194,0.25), transparent 60%)"}}/>
        <div className="relative text-center">
          <RobotFace expression="excited" size={280}/>
          <div className="mt-8 max-w-sm mx-auto px-6 text-white/80">
            <div className="text-2xl font-display text-white">You've been invited.</div>
            <p className="mt-2 text-sm">
              Set a password and you'll land as a co-admin of the Rammy console.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="text-sm text-[var(--rammy-text-muted)] hover:underline">← Back home</Link>

          {state.loading && (
            <div className="mt-10 text-sm text-[var(--rammy-text-muted)] font-mono" data-testid="invite-loading">Checking invite…</div>
          )}

          {!state.loading && !state.invite && (
            <div className="mt-8" data-testid="invite-invalid">
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
                <ShieldCheck className="text-red-600" size={22}/>
              </div>
              <h1 className="mt-4 text-3xl font-display">Invite unavailable</h1>
              <p className="mt-2 text-[var(--rammy-text-sec)]">{state.error || "This invite is invalid or expired."}</p>
              <Link to="/"><Button className="btn-pill btn-dark mt-6">Back to Rammy</Button></Link>
            </div>
          )}

          {state.invite && (
            <form onSubmit={submit} data-testid="invite-accept-form">
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] px-3 py-1 text-xs font-medium">
                <KeyRound size={12}/> Admin invite
              </div>
              <h1 className="mt-4 text-4xl font-display">Join the console.</h1>
              <p className="mt-2 text-[var(--rammy-text-sec)]">
                <span className="font-medium text-[var(--rammy-text)]">{state.invite.invited_by_email}</span> invited{" "}
                <span className="font-medium text-[var(--rammy-text)]">{state.invite.email}</span>.
              </p>

              <div className="mt-8 space-y-5">
                <div>
                  <Label>Email</Label>
                  <Input value={state.invite.email} disabled className="mt-2 h-12 rounded-2xl bg-[var(--rammy-surface-alt)]"/>
                </div>
                <div>
                  <Label htmlFor="name">Your name</Label>
                  <Input id="name" required value={name} onChange={e=>setName(e.target.value)} className="mt-2 h-12 rounded-2xl" data-testid="invite-name"/>
                </div>
                <div>
                  <Label htmlFor="password">Set a password (min 6)</Label>
                  <Input id="password" type="password" required minLength={6} value={password} onChange={e=>setPassword(e.target.value)} className="mt-2 h-12 rounded-2xl" data-testid="invite-password"/>
                </div>
                <Button type="submit" disabled={busy} className="btn-pill btn-primary w-full h-12" data-testid="invite-accept-submit">
                  {busy ? "Setting you up…" : "Accept invite & enter console"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
